<template>
  <modal-fullscreen
    title="Preview Document"
    :visible="visible"
    :destroy-on-close="true"
    width="600px"
    data-testid="document-preview-modal"
    v-if="visible"
    @cancel="closeModal"
  >
    <div v-if="loading || !document" class="loading-spin" style="height: 650px" data-testid="document-preview-loading">
      <a-spin />
    </div>

    <div v-else class="flex flex-column gap-3" data-testid="document-preview-content">
      <h4 class="m-0">{{ document.name }}</h4>
      <template v-if="document.type === 'pdf'"> <pdf-viewer :file="document.body.file.url" /></template>
      <template v-else-if="document.type === 'text'">
        <p class="m-0" v-html="safeDocumentBodyItems(document.body.items)"></p>
      </template>
      <template v-else-if="document.type === 'video'">
        <video
          v-if="document.body.file.url"
          class="ant-col-24 mt-5"
          :style="{ height: '336px' }"
          :src="document.body.file.url"
          controls
        ></video>
        <p v-if="document.body.details" class="m-0">Details: {{ document.body.details }}</p>
      </template>
      <template v-else-if="document.type === 'survey'">
        <p v-if="document.body.survey.details" class="m-0">Details: {{ document.body.survey.details }}</p>
        <a-progress
          v-if="document.body.survey.associate_result"
          :status="
            countCompletionRate(document.body.survey.associate_result?.completion_rate) >=
            document.body.survey.completion_rate
              ? 'success'
              : 'exception'
          "
          :percent="countCompletionRate(document.body.survey.associate_result?.completion_rate)"
        />
        <div class="questions-list">
          <div v-for="(question, idx) in document.body.survey.questions" :key="question.id">
            <div class="flex gap-2 mb-1">
              <a-badge :number-style="{ backgroundColor: '#3b82f6' }" :count="idx + 1" />

              {{ question.name }}
            </div>

            <div class="answers-list">
              <div
                class="answer"
                :style="{
                  borderColor: answer.correct ? '#10b981' : '#ef4444'
                }"
                v-for="answer in question.answers"
                :key="answer.id"
              >
                <a-checkbox
                  v-if="document.body.survey.associate_result"
                  :checked="checkedAnswer(answer, document.body.survey.associate_result?.answers)"
                />

                <answer-correctness-icon :correct="answer.correct" />
                {{ answer.name }}
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>

    <template slot="footer">
      <div class="flex justify-content-between" data-testid="document-preview-footer">
        <a-button @click="closeModal" data-testid="document-preview-close-button">Close</a-button>
      </div>
    </template>
  </modal-fullscreen>
</template>

<script>
import PdfViewer from '@/components/entities/viewer/pdf-viewer.vue'
import { Badge, Button, Checkbox, notification, Progress, Spin } from 'ant-design-vue'
import { joinHtmlItems } from '@/utils/safe-html'
import { countCompletionRate } from '@/utils/survey'
import { createNamespacedHelpers } from 'vuex'
import AnswerCorrectnessIcon from './answer-correctness-icon.vue'

const { mapActions: documentActions } = createNamespacedHelpers('document')

export default {
  components: {
    'a-button': Button,
    'a-spin': Spin,
    'a-progress': Progress,
    'a-checkbox': Checkbox,
    'a-badge': Badge,
    'pdf-viewer': PdfViewer,
    'answer-correctness-icon': AnswerCorrectnessIcon
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    document_id: {
      type: Number,
      default: undefined
    },
    version: {
      type: Number,
      default: undefined
    }
  },
  data() {
    return {
      loading: false,

      document: undefined
    }
  },
  watch: {
    visible: {
      handler(newVal) {
        if (newVal) {
          this.fetchDocument()
        }
      },
      immediate: true
    }
  },

  methods: {
    ...documentActions(['getPreviewDocument']),

    showNotification(type, message, description) {
      notification[type]({
        message,
        description
      })
    },

    safeDocumentBodyItems(items) {
      return joinHtmlItems(items)
    },

    checkedAnswer(answer, result) {
      return result.some(item => item.id === answer.id)
    },

    countCompletionRate,

    closeModal() {
      this.$emit('update:visible', false)
      this.$emit('update:document_id', undefined)
      this.$emit('update:version', undefined)
    },

    async fetchDocument() {
      try {
        this.loading = true

        const payload = {
          document_id: this.document_id,
          version_number: this.version
        }

        const data = await this.getPreviewDocument(payload)
        this.document = data
      } catch (error) {
        this.showNotification('error', 'Error', typeof error === 'string' ? error : error?.message)
        this.closeModal()
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.questions-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-height: 300px;
  overflow: auto;
  border: 1px solid var(--border-color-base);
  border-radius: 10px;
  padding: 16px;
}

.answers-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.answer {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  border: 1px solid var(--border-color-base);
  border-radius: 10px;
}
</style>
